import { useCallback, useState } from "react";
import { ApiError } from "./custom-fetch";

export type FieldErrors = Record<string, string>;

export interface ApiErrorEnvelope {
  message: string | null;
  fieldErrors: FieldErrors;
  code: string | null;
}

const EMPTY: ApiErrorEnvelope = { message: null, fieldErrors: {}, code: null };

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * Extract the standard `{ message, fieldErrors, code? }` envelope from any
 * error thrown by the generated API client. Falls back to `error.message`
 * when the server didn't send the envelope (e.g. network errors, non-JSON
 * responses).
 */
export function extractApiErrorEnvelope(err: unknown): ApiErrorEnvelope {
  if (err instanceof ApiError && isPlainRecord(err.data)) {
    const body = err.data;
    const fieldErrorsRaw = body.fieldErrors;
    const fieldErrors: FieldErrors = {};
    if (isPlainRecord(fieldErrorsRaw)) {
      for (const [k, v] of Object.entries(fieldErrorsRaw)) {
        if (typeof v === "string") fieldErrors[k] = v;
        else if (Array.isArray(v) && typeof v[0] === "string") fieldErrors[k] = v[0];
      }
    }
    const message = readString(body.message) ?? readString(body.error);
    const code = readString(body.code);
    return { message, fieldErrors, code };
  }
  if (err instanceof Error) {
    return { message: err.message, fieldErrors: {}, code: null };
  }
  if (typeof err === "string") {
    return { message: err, fieldErrors: {}, code: null };
  }
  return EMPTY;
}

export interface UseFieldErrorsResult {
  /** Per-field error messages keyed by field name (e.g. "email"). */
  fieldErrors: FieldErrors;
  /** Top-level form/banner message (server message, network error, etc.). */
  formError: string | null;
  /** Server-provided code (e.g. "invalid_credentials"), when present. */
  code: string | null;
  /** Capture the error from a failed mutation. */
  setFromError: (err: unknown) => ApiErrorEnvelope;
  /** Reset everything (call on submit, on field change, on dialog open). */
  clear: () => void;
  /** Look up the error for a single field, if any. */
  fieldError: (name: string) => string | undefined;
  /** Clear a single field's error (call from onChange to clear stale errors). */
  clearField: (name: string) => void;
}

/**
 * React hook for converting an `ApiError` thrown by the generated client into
 * per-field state. Renders the `{ message, fieldErrors }` envelope returned
 * by `respondZodError` / `respondError` on the server.
 */
export function useFieldErrors(): UseFieldErrorsResult {
  const [state, setState] = useState<ApiErrorEnvelope>(EMPTY);

  const setFromError = useCallback((err: unknown): ApiErrorEnvelope => {
    const envelope = extractApiErrorEnvelope(err);
    setState(envelope);
    return envelope;
  }, []);

  const clear = useCallback(() => setState(EMPTY), []);

  const fieldError = useCallback(
    (name: string): string | undefined => state.fieldErrors[name],
    [state.fieldErrors],
  );

  const clearField = useCallback((name: string) => {
    setState((prev: ApiErrorEnvelope) => {
      if (!(name in prev.fieldErrors)) return prev;
      const next = { ...prev.fieldErrors };
      delete next[name];
      return { ...prev, fieldErrors: next };
    });
  }, []);

  return {
    fieldErrors: state.fieldErrors,
    formError: state.message,
    code: state.code,
    setFromError,
    clear,
    fieldError,
    clearField,
  };
}
